import axiosInstance from "@/apis/axios-instance";
import type {
  IxdMatchPreviewResult,
  IxdValueSyncPlanResult,
  IxdValueSyncRequest,
  IxdValueSyncResult,
  WorkbookTablePreviewResult,
} from "./types";

export const getIxdMatchPreview = async (
  workbookId: string,
  reportId: string,
): Promise<IxdMatchPreviewResult> => {
  const response = await axiosInstance.get(
    `/v2/xbrl-view/workbooks/${workbookId}/ixd-value-sync/preview`,
    {
      params: { reportId },
      headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
    },
  );
  const data = response.data;
  if (
    !data ||
    typeof data.workbookName !== "string" ||
    typeof data.ixdName !== "string" ||
    !Array.isArray(data.ixdToc) ||
    !Array.isArray(data.entries)
  ) {
    throw new Error("IXD 표 매칭 미리보기 응답 형식이 올바르지 않습니다.");
  }
  return data as IxdMatchPreviewResult;
};

/**
 * 매칭 뷰의 워크북 표 하나를 다시 그린다 — 자동합계 토글 뒤 그 표만 갱신할 때.
 *
 * transpose는 매칭된 IXD 표의 방향(entry.ixd.dlView.isTransposed)이다. 전역 단계
 * (아카이브 다운로드·구조 매칭·compose)를 밟지 않으므로 preview 전체보다 훨씬 싸다.
 */
export const getIxdMatchWorkbookTable = async (
  workbookId: string,
  tableId: string,
  params: { roleKey: string; transpose: boolean },
): Promise<WorkbookTablePreviewResult> => {
  const response = await axiosInstance.get(
    `/v2/xbrl-view/workbooks/${workbookId}/ixd-value-sync/preview/tables/${tableId}`,
    { params },
  );
  const data = response.data;
  if (!data || !("current" in data) || !("prior" in data)) {
    throw new Error("워크북 표 미리보기 응답 형식이 올바르지 않습니다.");
  }
  return data as WorkbookTablePreviewResult;
};

/**
 * 동기화하면 무엇이 반영될지 계산해서 돌려준다. IXD report를 건드리지 않는다.
 *
 * plan과 POST는 같은 계산을 쓰므로 여기서 본 entries가 그대로 반영된다 — 단
 * 그 사이에 워크북 태깅이나 IXD report가 바뀌면 결과도 달라진다. 확인 화면을
 * 오래 띄워두는 UX라면 실행 직전 다시 부른다.
 *
 * chunked transfer로 내려오므로 Content-Length가 없다. 진행률 표시를 여기에
 * 기대는 코드는 동작하지 않는다.
 */
export const getIxdValueSyncPlan = async (
  workbookId: string,
  reportId: string,
): Promise<IxdValueSyncPlanResult> => {
  const response = await axiosInstance.get(
    `/v2/xbrl-view/workbooks/${workbookId}/ixd-value-sync/plan`,
    {
      params: { reportId },
      headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
    },
  );
  const data = response.data;
  if (
    !data ||
    typeof data.plannedCount !== "number" ||
    typeof data.failureCount !== "number" ||
    typeof data.interpolationCount !== "number" ||
    !Array.isArray(data.tables)
  ) {
    throw new Error("IXD 값 동기화 계획 응답 형식이 올바르지 않습니다.");
  }
  const plan = data as IxdValueSyncPlanResult;
  // 서버가 값 없음을 "None"으로 내려보낸다 — 셀에 찍히지 않게 빈 값으로 바꾼다
  for (const table of plan.tables) {
    for (const entry of table.entries) {
      if (entry.value == null || entry.value === "None") entry.value = "";
    }
  }
  return plan;
};

/**
 * 계획을 그대로 IXD report에 반영한다. composite key가 겹치면 갱신, 없으면 삽입.
 *
 * 되돌리는 API는 없다. failures가 있는 상태로 실행하면 그 셀들은 반영되지 않은
 * 채 나머지가 반영된다 — 부분 반영이 정상 동작이므로 호출 전 확인 단계를
 * 건너뛰지 않는다.
 */
export const postIxdValueSync = async (
  workbookId: string,
  reportId: string,
): Promise<IxdValueSyncResult> => {
  const body: IxdValueSyncRequest = { reportId };
  const response = await axiosInstance.post(
    `/v2/xbrl-view/workbooks/${workbookId}/ixd-value-sync`,
    body,
  );
  const data = response.data;
  if (
    !data ||
    typeof data.syncedCount !== "number" ||
    !Array.isArray(data.failures) ||
    !Array.isArray(data.interpolations)
  ) {
    throw new Error("IXD 값 동기화 응답 형식이 올바르지 않습니다.");
  }
  return data as IxdValueSyncResult;
};
